<!-- Using this opportunity to also grab the "default value" for naked mode -->

<template>
  <Dialog title="Switch to Naked mode?" @close="$emit('close')">
    <p>
      Naked mode allows you to fully customize the structure & style of your
      email. Learn more about it
      <a href="https://docs.buttondown.com/naked-mode" target="_blank">here</a>.
    </p>

    <div class="-mx-4 mt-4 border-y border-border">
      <Callout.Root variant="warning">
        <template #title>No safe way back.</template>

        Switching to another mode later will reset your email body completely
        clean.
      </Callout.Root>
    </div>

    <template #actions>
      <ActionButton
        text="Switch"
        variant="primary"
        :disabled="!defaultHtml"
        :pending="!defaultHtml"
        @click="handleConfirm"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
import { computed } from "vue";

import ActionButton from "@/components/utilities/ActionButton.vue";
import Callout from "@/design_system/Callout";
import Dialog from "@/design_system/shared/Dialog/Dialog.vue";
import {
  type EmailTemplate,
  useDraftPreviewQuery,
} from "@/lib/api/queries/emails/render";
import { useNewsletter } from "@/store/stores/account";

const newsletter = useNewsletter();

const emit = defineEmits<{
  close: [];
  submit: [defaultBody: string];
}>();

const props = defineProps<{
  email: {
    id?: string;
    subject: string;
    body: string;
    template?: EmailTemplate | null;
  };
}>();

const render = useDraftPreviewQuery({
  enabled: computed(() => true),
  options: computed(() => {
    const email = props.email;

    let template = email.template;
    if (template == null || template === "naked") {
      template =
        (newsletter.value.template as EmailTemplate | undefined) ?? "modern";
    }

    return {
      type: "email_draft",
      email: {
        id: email.id,
        subject: email.subject,
        body: email.body,
        template: template,
      },
      previews: ["email"],
    };
  }),
});

const defaultHtml = computed(() => {
  const html = render.data.value?.email;
  if (html === undefined) {
    return;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const unsub_link = doc.querySelector(".email-actions--unsubscribe a");
  const subject_link = doc.querySelector(".subject a");

  if (unsub_link) {
    // remove the literal unsubscribe url for a specific subscriber with a template tag
    unsub_link.setAttribute("href", "{{ unsubscribe_url }}");
  }

  if (subject_link) {
    // remove the literal email archive url for a specific subscriber with a template tag
    subject_link.setAttribute("href", "{{ email.absolute_url }}");
  }

  return doc.documentElement.outerHTML;
});

const handleConfirm = () => {
  if (!defaultHtml.value) {
    return;
  }

  emit("close");
  emit("submit", defaultHtml.value);
};
</script>
